import { useState, useEffect, useCallback } from 'react';
import { settings, dispatchSettingChange, onSettingChange } from '../services/settingsService';

/**
 * Hook for managing a boolean setting with automatic sync across components
 * @param {string} name - Setting name (shake, bluetooth, voiceTrigger, audioUpload)
 * @param {object} options - Optional callbacks for mutual exclusion
 */
export const useSetting = (name, options = {}) => {
  const setting = settings[name];
  const [enabled, setEnabled] = useState(setting.isEnabled());

  // Listen for changes from other components
  useEffect(() => {
    return onSettingChange(name, ({ enabled: newEnabled }) => {
      setEnabled(newEnabled);
    });
  }, [name]);

  // Toggle handler with optional mutual exclusion
  const toggle = useCallback(() => {
    const newValue = !enabled;
    setEnabled(newValue);

    if (newValue) {
      setting.enable();
      // Handle mutual exclusions
      options.disableWhenEnabled?.forEach(otherName => {
        settings[otherName].disable();
        dispatchSettingChange(otherName, { enabled: false });
      });
    } else {
      setting.disable();
    }

    dispatchSettingChange(name, { enabled: newValue, ...options.extraDetail?.() });
  }, [enabled, name, setting, options]);

  return [enabled, toggle, setEnabled];
};

/**
 * Hook for voice trigger setting with trigger word
 * When enabled, also enables stopTrigger. When disabled, also disables stopTrigger.
 */
export const useVoiceTriggerSetting = () => {
  const [enabled, baseToggle] = useSetting('voiceTrigger', {
    disableWhenEnabled: ['audioUpload'],
    extraDetail: () => ({ triggerWord: settings.voiceTrigger.word.get() })
  });

  const [triggerWord, setTriggerWordState] = useState(settings.voiceTrigger.word.get());

  // Enhanced toggle that syncs stopTrigger with voiceTrigger
  const toggle = useCallback(() => {
    baseToggle();
    // Sync stopTrigger to match voiceTrigger's new state
    const newEnabled = !enabled;
    if (newEnabled) {
      settings.stopTrigger.enable();
    } else {
      settings.stopTrigger.disable();
    }
    dispatchSettingChange('stopTrigger', { enabled: newEnabled, stopWord: settings.stopTrigger.word.get() });
  }, [enabled, baseToggle]);

  const setTriggerWord = useCallback((word) => {
    setTriggerWordState(word);
    settings.voiceTrigger.word.set(word);
    dispatchSettingChange('voiceTrigger', { enabled, triggerWord: word });
  }, [enabled]);

  // Listen for trigger word changes
  useEffect(() => {
    return onSettingChange('voiceTrigger', ({ triggerWord: newWord }) => {
      if (newWord) setTriggerWordState(newWord);
    });
  }, []);

  return { enabled, toggle, triggerWord, setTriggerWord };
};

/**
 * Hook for audio upload setting (mutually exclusive with voice trigger)
 */
export const useAudioUploadSetting = () => {
  return useSetting('audioUpload', {
    disableWhenEnabled: ['voiceTrigger']
  });
};

/**
 * Hook for stop trigger setting with stop word
 */
export const useStopTriggerSetting = () => {
  const [enabled, toggle] = useSetting('stopTrigger', {
    extraDetail: () => ({ stopWord: settings.stopTrigger.word.get() })
  });

  const [stopWord, setStopWordState] = useState(settings.stopTrigger.word.get());

  const setStopWord = useCallback((word) => {
    setStopWordState(word);
    settings.stopTrigger.word.set(word);
    dispatchSettingChange('stopTrigger', { enabled, stopWord: word });
  }, [enabled]);

  // Listen for stop word changes
  useEffect(() => {
    return onSettingChange('stopTrigger', ({ stopWord: newWord }) => {
      if (newWord) setStopWordState(newWord);
    });
  }, []);

  return { enabled, toggle, stopWord, setStopWord };
};

/**
 * Hook for trigger language setting (independent from UI language)
 */
export const useTriggerLanguageSetting = () => {
  const [language, setLanguageState] = useState(settings.triggerLanguage.get());

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    settings.triggerLanguage.set(lang);
    dispatchSettingChange('triggerLanguage', { language: lang });
  }, []);

  // Listen for language changes
  useEffect(() => {
    return onSettingChange('triggerLanguage', ({ language: newLang }) => {
      if (newLang) setLanguageState(newLang);
    });
  }, []);

  return { language, setLanguage };
};

/**
 * Hook for upload raw chunks setting (debug feature)
 */
export const useUploadRawChunksSetting = () => {
  return useSetting('uploadRawChunks');
};

/**
 * Hook for playback raw setting (show raw audio player in detail view)
 */
export const usePlaybackRawSetting = () => {
  return useSetting('playbackRaw');
};

/**
 * Hook for chunk duration setting
 */
export const useChunkDurationSetting = () => {
  const [duration, setDurationState] = useState(settings.chunkDuration.get());

  const setDuration = useCallback((value) => {
    const bounded = Math.max(settings.chunkDuration.min, Math.min(settings.chunkDuration.max, value));
    setDurationState(bounded);
    settings.chunkDuration.set(bounded);
    dispatchSettingChange('chunkDuration', { duration: bounded });
  }, []);

  // Listen for duration changes
  useEffect(() => {
    return onSettingChange('chunkDuration', ({ duration: newDuration }) => {
      if (newDuration !== undefined) setDurationState(newDuration);
    });
  }, []);

  return {
    duration,
    setDuration,
    min: settings.chunkDuration.min,
    max: settings.chunkDuration.max,
    default: settings.chunkDuration.default
  };
};

/**
 * Hook for noise level setting (0=quiet to 4=very loud)
 */
export const useNoiseLevelSetting = () => {
  const [level, setLevelState] = useState(settings.noiseLevel.get());

  const setLevel = useCallback((value) => {
    const bounded = Math.max(0, Math.min(4, value));
    setLevelState(bounded);
    settings.noiseLevel.set(bounded);
    dispatchSettingChange('noiseLevel', { level: bounded });
  }, []);

  useEffect(() => {
    return onSettingChange('noiseLevel', ({ level: newLevel }) => {
      if (newLevel !== undefined) setLevelState(newLevel);
    });
  }, []);

  return { level, setLevel };
};

/**
 * Hook for pause detection setting (0=sensitive to 4=relaxed)
 */
export const usePauseDetectionSetting = () => {
  const [level, setLevelState] = useState(settings.pauseDetection.get());

  const setLevel = useCallback((value) => {
    const bounded = Math.max(0, Math.min(4, value));
    setLevelState(bounded);
    settings.pauseDetection.set(bounded);
    dispatchSettingChange('pauseDetection', { level: bounded });
  }, []);

  useEffect(() => {
    return onSettingChange('pauseDetection', ({ level: newLevel }) => {
      if (newLevel !== undefined) setLevelState(newLevel);
    });
  }, []);

  return { level, setLevel };
};

/**
 * Hook for extended error reports setting
 */
export const useExtendedErrorReportsSetting = () => {
  return useSetting('extendedErrorReports');
};

/**
 * Hook for danger zone enabled setting (admin control)
 */
export const useDangerZoneEnabledSetting = () => {
  return useSetting('dangerZoneEnabled');
};

/**
 * Hook for beep volume setting (0=off to 100=max)
 */
export const useBeepVolumeSetting = () => {
  const [volume, setVolumeState] = useState(settings.beepVolume.get());

  const setVolume = useCallback((value) => {
    const bounded = Math.max(0, Math.min(100, value));
    setVolumeState(bounded);
    settings.beepVolume.set(bounded);
    dispatchSettingChange('beepVolume', { volume: bounded });
  }, []);

  useEffect(() => {
    return onSettingChange('beepVolume', ({ volume: newVolume }) => {
      if (newVolume !== undefined) setVolumeState(newVolume);
    });
  }, []);

  return { volume, setVolume };
};
